import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { SectionView, REPORT_PART_LABELS, REPORT_PART_ORDER } from '@rb/ui'
import type { ReportPart } from '@rb/ui'
import { DEMO_HOTEL_REPORT } from '../data/demoHotelReport'
import styles from './DemoEmbed.module.css'

// Short blurbs under each card label — purely cosmetic for the demo.
const PART_BLURB: Record<ReportPart, string> = {
  analyse: 'Verbrauch & Lastprofil',
  vorschlag: 'Empfohlene Maßnahmen',
  roi: 'Wirtschaftlichkeit',
  finanzierung: 'Förderung & Modell',
}

/**
 * Standalone, embeddable demo of one analysis — designed to be dropped into an
 * <iframe> (e.g. Elementor). No AuthGuard, no API/D1/auth: it renders the
 * hardcoded DEMO_HOTEL_REPORT through the same SectionView/BlockRenderer the
 * real client pages use.
 *
 * Self-contained: it sets the theme explicitly on the document root (does NOT
 * inherit the parent page's [data-theme]) and posts its height to the parent
 * window so the host iframe can auto-resize.
 *
 * Layout mirrors the platform report detail: part cards on top, then a section
 * table-of-contents on the left + the content on the right. In the auto-resize
 * embed the iframe grows to full height (the host page scrolls), so the TOC is
 * a non-sticky index; clicking scrolls the section into view when /demo is
 * opened directly.
 *
 * Query params:
 *   ?theme=light | dark   (default: dark)
 *   ?part=analyse|vorschlag|roi|finanzierung   (initial card; default: analyse)
 */
export default function DemoEmbed() {
  const params = new URLSearchParams(window.location.search)
  const themeParam = params.get('theme') === 'light' ? 'light' : 'dark'
  const partParam = params.get('part')
  const initialPart: ReportPart = REPORT_PART_ORDER.includes(partParam as ReportPart)
    ? (partParam as ReportPart)
    : 'analyse'

  const [activePart, setActivePart] = useState<ReportPart>(initialPart)
  const [activeSection, setActiveSection] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Theme is set explicitly on the document root so token colors resolve
  // regardless of the embedding page. Restore on unmount to be polite.
  useLayoutEffect(() => {
    const prev = document.documentElement.dataset.theme
    document.documentElement.dataset.theme = themeParam
    document.documentElement.classList.add('rb-demo-embed')
    return () => {
      if (prev) document.documentElement.dataset.theme = prev
      else delete document.documentElement.dataset.theme
      document.documentElement.classList.remove('rb-demo-embed')
    }
  }, [themeParam])

  // Auto-resize: post our content height to the parent frame on any layout
  // change (part switch, font load, viewport). The parent listens for
  // { type: 'rb-demo-resize', height } and sets the iframe height.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    function postHeight() {
      const height = Math.ceil(el!.getBoundingClientRect().height)
      window.parent?.postMessage({ type: 'rb-demo-resize', height }, '*')
    }
    postHeight()
    const ro = new ResizeObserver(postHeight)
    ro.observe(el)
    window.addEventListener('load', postHeight)
    return () => {
      ro.disconnect()
      window.removeEventListener('load', postHeight)
    }
  }, [])

  // Reset the active section whenever the part changes.
  useEffect(() => {
    setActiveSection(0)
  }, [activePart])

  // Scroll-spy: when the page actually scrolls (e.g. /demo opened directly, or
  // a fixed-height embed), highlight the section nearest the top. In the
  // auto-resize embed the iframe doesn't scroll, so this simply stays put.
  useEffect(() => {
    const root = contentRef.current
    if (!root) return
    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-section-index]'))
    if (els.length < 2) return
    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        const idx = top && (top.target as HTMLElement).dataset.sectionIndex
        if (idx != null) setActiveSection(Number(idx))
      },
      { rootMargin: '-80px 0px -55% 0px', threshold: 0 }
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [activePart])

  function goToSection(i: number) {
    setActiveSection(i)
    contentRef.current
      ?.querySelector<HTMLElement>(`[data-section-index="${i}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const sections = DEMO_HOTEL_REPORT[activePart]
  const showToc = sections.length > 1

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.inner}>
        <header className={styles.head}>
          <h1 className={styles.title}>Energieanalyse</h1>
          {DEMO_HOTEL_REPORT.cover?.subtitle && (
            <p className={styles.subtitle}>
              {DEMO_HOTEL_REPORT.cover.subtitle}
              {DEMO_HOTEL_REPORT.cover.period ? ` · ${DEMO_HOTEL_REPORT.cover.period}` : ''}
            </p>
          )}
        </header>

        <nav className={styles.cards} aria-label="Berichtsteile">
          {REPORT_PART_ORDER.map((part, i) => {
            const isActive = part === activePart
            const isEmpty = DEMO_HOTEL_REPORT[part].length === 0
            return (
              <button
                key={part}
                type="button"
                className={[styles.card, isActive ? styles.cardActive : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={isActive}
                aria-current={isActive ? 'true' : undefined}
                disabled={isEmpty}
                onClick={() => setActivePart(part)}
              >
                <span className={styles.cardIndex}>{String(i + 1).padStart(2, '0')}</span>
                <span className={styles.cardLabel}>{REPORT_PART_LABELS[part]}</span>
                <span className={styles.cardBlurb}>{PART_BLURB[part]}</span>
              </button>
            )
          })}
        </nav>

        <div
          className={[styles.layout, showToc ? '' : styles.layoutNoToc].filter(Boolean).join(' ')}
          key={activePart}
        >
          {showToc && (
            <nav className={styles.toc} aria-label="Abschnitte">
              <ul className={styles.tocList}>
                {sections.map((section, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className={[styles.tocItem, activeSection === i ? styles.tocItemActive : '']
                        .filter(Boolean)
                        .join(' ')}
                      aria-current={activeSection === i ? 'true' : undefined}
                      onClick={() => goToSection(i)}
                    >
                      {section.section_title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div className={styles.content} ref={contentRef}>
            {sections.length === 0 ? (
              <p className={styles.empty}>Für diesen Bereich liegen keine Inhalte vor.</p>
            ) : (
              sections.map((section, i) => (
                <section
                  key={i}
                  data-section-index={i}
                  className={styles.sectionBlock}
                >
                  <h2 className={styles.sectionTitle}>{section.section_title}</h2>
                  <SectionView section={section} />
                </section>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
